// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { Building2 } from 'lucide-react'
import { listFacilities, listSiteOfCareTypes, type FacilityRow, type SiteOfCareType } from '../../api/client'
import { useSetUserFacilityAccess, useUserFacilityAccess } from '../../hooks/queries/siteOfCare'
import { friendlyError } from '../../api/errors'

/**
 * D-SOC-1 — the facility grant panel: which Site of Care facilities a "Facility Staff" account may
 * see. A shared COMPONENT (not page code) for the same reason ManufacturerAccessSection is: both
 * user-management surfaces (UsersTab and ManageUsersPage) assign the role, so both must offer the
 * grant, or the role goes on with no way to scope it and the portal shows nothing.
 *
 * Mirrors ManufacturerAccessSection's props exactly so it drops into the same slots.
 */
export default function FacilityAccessSection({ userId, roles, canWrite, onChanged }: {
  userId: string
  roles: { id: string; name: string }[]
  canWrite: boolean
  onChanged: () => void
}) {
  // Match on role NAME (what the admin just ticked), not the permission-derived legacy role, which
  // only refreshes on the user's next session.
  // D-SOC-3: providers can be scoped to facilities too, so the grant panel shows for them as well.
  const show = roles.some(r => r.name === 'Facility Staff' || r.name === 'Provider' || r.name === 'Provider Nurse')
  const accessQ = useUserFacilityAccess(userId, show)
  const save = useSetUserFacilityAccess(userId)

  const [dir, setDir] = useState<FacilityRow[]>([])
  const [types, setTypes] = useState<SiteOfCareType[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [dirty, setDirty] = useState(false)
  const [q, setQ] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    // A PICKER over the whole directory, not a paged list: ask for the server's maximum page.
    // listFacilities became PAGED and now defaults to 50 — with 54 facilities imported, four of
    // them silently vanished from this grant list and could not be granted to anyone.
    Promise.all([listFacilities(undefined, true, { page: 1, pageSize: 200 }), listSiteOfCareTypes(true)])
      .then(([f, t]) => {
        setDir(f.items)
        setTypes(t.items)
        // Say so rather than quietly showing a subset: a grant screen that hides facilities
        // hands out access the admin did not intend to withhold.
        const total = f.total ?? f.items.length
        if (total > f.items.length)
          setErr(`Showing the first ${f.items.length} of ${total} facilities — narrow the directory or raise the page size before granting.`)
      })
      .catch(e => setErr(friendlyError(e)))
  }, [show])

  useEffect(() => {
    if (accessQ.data) { setSelected(accessQ.data.grants.map(g => g.facility_id)); setDirty(false) }
  }, [accessQ.data])

  const typeLabel = useMemo(() => Object.fromEntries(types.map(t => [t.code, t.label.split(' — ')[0]])), [types])
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase()
    return dir.filter(f => !n || f.name.toLowerCase().includes(n) || (f.city ?? '').toLowerCase().includes(n) || (f.zip_code ?? '').includes(n))
  }, [dir, q])

  if (!show) return null

  const toggle = (id: string) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]); setDirty(true); setNote(null)
  }
  const doSave = () => {
    setErr(null)
    save.mutate(selected, {
      onSuccess: r => { setNote(`Saved — this user can see ${r.count} facilit${r.count === 1 ? 'y' : 'ies'}.`); setDirty(false); onChanged() },
      onError: e => setErr(friendlyError(e)),
    })
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Building2 size={14} /> Facilities this user can see
      </h4>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>
        Used when the site of care scope above is “Facility residents only”: the account sees prescriptions only for residents whose Rx address maps to a granted facility. Pick at least one — with none picked this account sees nothing.
      </div>

      {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
      {note && <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{note}</div>}
      {accessQ.isError && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', marginBottom: 8 }}>Could not load the current grant — {friendlyError(accessQ.error)}</div>}

      {selected.length === 0 && !accessQ.isLoading && (
        <div style={{ fontSize: 12.5, color: 'var(--color-warning, #ca8a04)', marginBottom: 8 }}>
          No facility granted yet — this user's portal will be empty until one is selected.
        </div>
      )}

      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search facilities by name, city or zip…"
        aria-label="Search facilities"
        style={{ width: '100%', height: 32, padding: '0 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
        {dir.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12.5, color: 'var(--color-text-mute)' }}>
            No facilities in the Site of Care directory yet — add them under Pharmacy settings → Site of Care.
          </div>
        ) : filtered.map(f => (
          <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderBottom: '1px solid var(--color-border)', cursor: canWrite ? 'pointer' : 'default', fontSize: 13 }}>
            <input type="checkbox" checked={selected.includes(f.id)} disabled={!canWrite || save.isPending} onChange={() => toggle(f.id)} />
            <span style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{f.name}</span>
              <span style={{ color: 'var(--color-text-mute)', marginLeft: 8, fontSize: 12 }}>
                {typeLabel[f.facility_type ?? ''] ?? f.facility_type} · {[f.city, f.zip_code].filter(Boolean).join(' ')}
              </span>
            </span>
          </label>
        ))}
        {dir.length > 0 && filtered.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: 'var(--color-text-mute)' }}>No facility matches “{q}”.</div>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--color-text-mute)', flex: 1 }}>{selected.length} selected</span>
        {canWrite && (
          <button type="button" onClick={doSave} disabled={!dirty || save.isPending}
            style={{ fontSize: 12.5, padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #185FA5)', color: '#fff', fontWeight: 600, cursor: dirty ? 'pointer' : 'default', opacity: dirty ? 1 : .55 }}>
            {save.isPending ? 'Saving…' : 'Save facility access'}
          </button>
        )}
      </div>
    </div>
  )
}
